import { z } from "zod";
import {
  COLLECTION_GRACE_DAYS_MAX,
  COLLECTION_GRACE_DAYS_MIN,
  OVERPAY_CREDIT_POLICIES,
} from "@goppy/domain";

/**
 * Contract config ngưỡng thu (US-E16.F6-0187 · @UC9249) — single source api ↔ admin ↔ worker key.
 *
 * 3 khoá registry (`system_settings`, override được ở Group.settings / Fund.settings):
 *  - `collection_grace_days` (int [0,90]) — ân hạn trước khi due bị đánh overdue.
 *  - `allow_partial_pay` (bool) — bật/tắt đóng một phần (F4).
 *  - `overpay_credit_policy` (credit|refund|reject) — xử lý phần đóng dư (F4).
 *
 * Biên schema DERIVE từ domain (`@goppy/domain` collection-config) — KHÔNG nhân bản hằng biên.
 * KHÔNG tiền ở đây (grace = số NGÀY) ⇒ number an toàn; tiền vẫn là string BigInt ở payments.ts.
 */

/** Key wire (snake_case) — dùng bởi api resolver, admin UI, worker (worker copy hằng, không dep). */
export const COLLECTION_GRACE_DAYS_KEY = "collection_grace_days";
export const ALLOW_PARTIAL_PAY_KEY = "allow_partial_pay";
export const OVERPAY_CREDIT_POLICY_KEY = "overpay_credit_policy";

/** Thứ tự ổn định cho UI/seed/test. */
export const COLLECTION_CONFIG_KEY_LIST = [
  COLLECTION_GRACE_DAYS_KEY,
  ALLOW_PARTIAL_PAY_KEY,
  OVERPAY_CREDIT_POLICY_KEY,
] as const;

/** Chính sách overpay (enum domain — KHÔNG bịa lại danh sách). */
export const overpayCreditPolicySchema = z.enum(
  OVERPAY_CREDIT_POLICIES as unknown as [string, ...string[]],
);
export type OverpayCreditPolicyContract = z.infer<typeof overpayCreditPolicySchema>;

/** Patch value từng khoá (validate biên tại biên API; domain validate LẠI khi ghi — fail-closed). */
export const collectionGraceDaysPatchSchema = z
  .number()
  .int()
  .min(COLLECTION_GRACE_DAYS_MIN)
  .max(COLLECTION_GRACE_DAYS_MAX);

export const allowPartialPayPatchSchema = z.boolean();

export const overpayCreditPolicyPatchSchema = overpayCreditPolicySchema;

/** Effective config thu của 1 quỹ (fund > group > system). `.strict()` — không lọt field lạ. */
export const collectionConfigResponseSchema = z
  .object({
    collectionGraceDays: collectionGraceDaysPatchSchema,
    allowPartialPay: allowPartialPayPatchSchema,
    overpayCreditPolicy: overpayCreditPolicyPatchSchema,
  })
  .strict();

export type CollectionConfigResponse = z.infer<typeof collectionConfigResponseSchema>;
